/* eslint @typescript-eslint/no-empty-interface: "off" */
import axios from "axios";
import sequenceInstance from "contexts/SessionSequenceProvider";
import { useAppSelector } from "hooks";
import { DateTime } from "luxon";
import { Moment } from "moment";
import { createContext, useContext, useState } from "react";
import { browserName, isMobile } from "react-device-detect";
import { getIsStandalonePWA } from "components/PWA/helpers/getIsStandalonePWA";
import { getPlatform } from "utils/platform";
import { useDispatch } from "react-redux";
import { offersApi } from "redux/apis";
import { isDebugEnabled, selectWebStoreState } from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { useEnvContext } from "../../contexts/EnvProvider";
import { useGoogleAnalyticsContext } from "../../contexts/GoogleAnalyticsProvider";
import { analyticsTrackerV2 } from "contexts/ScopelyIdentityProvider/configv2";
import { WebStoreContext } from "../../contexts/WebStoreProvider";
import {
	IAbstractResponse,
	IAuthResponse,
	IAuthToken,
	IUserTracker
} from "../../contexts/WebStoreProvider/types";
import { getDebugLogger } from "../../utils/debugLogger";

export const LOCAL_STORAGE_DATE_JUMPED =
	"store.solitairetripeaks.com:dateJumped";

const debug = getDebugLogger({
	isEnabled: true
});
/* eslint-disable-next-line */
const debugLogError = (name: string, error: any) => {
	debug.log({
		iconText: `${name}: [error]`,
		message: [error],
		iconColor: "red"
	});
};

/* eslint-disable */
export interface IDebugWebStoreContext {
	timeJump(
		nowTime: Moment | null,
		deltaDays: number,
		deltaHours?: number,
		deltaMinutes?: number
	): Promise<boolean>;
	resetTimeJump(): Promise<boolean>;
	setLoyaltyPoints(points: number): Promise<void>;
	setServerTime(now: string): Promise<boolean>;
	authToken: IAuthToken | null;
	setAuthTokenTtl(authTokenTtl: number): Promise<boolean>;
	clearInboxMessages(): Promise<boolean>;
	clearFirstLoginReward(): Promise<boolean>;
	clearUser(): Promise<boolean>;
	enableDebugAccess(): Promise<void>;
	handleRefresh(): void;
}
/* eslint-enable */

export interface IUserDataResponse extends IAbstractResponse {
	userData: { userTracker: IUserTracker };
}

export const DebugWebStoreContext = createContext<IDebugWebStoreContext>({
	timeJump: async (): Promise<boolean> => {
		return false;
	},
	resetTimeJump: async (): Promise<boolean> => {
		return false;
	},
	// eslint-disable-next-line
	setLoyaltyPoints: async (points: number): Promise<void> => {
		// do nothing
	},
	// eslint-disable-next-line
	setServerTime: async (now: string): Promise<boolean> => {
		return false;
	},
	// eslint-disable-next-line
	setAuthTokenTtl: async (authTokenTtl: number): Promise<boolean> => {
		return false;
	},
	clearInboxMessages: async (): Promise<boolean> => {
		return false;
	},
	clearFirstLoginReward: async (): Promise<boolean> => {
		return false;
	},
	clearUser: async (): Promise<boolean> => {
		return false;
	},
	enableDebugAccess: async (): Promise<void> => {
		// do nothing
	},
	handleRefresh: async () => {
		// no op
	},
	authToken: null
});
/* eslint-disable-next-line */
function DebugWebStoreProvider({ children }: any) {
	const {
		authToken,
		handleErrorResponse,
		userTracker,
		createHeaders,
		fbAccessToken,
		setAuthToken,
		forceReloadStoreRaw,
		syncUserTracker,
		logout,
		setNowTime
	} = useContext(WebStoreContext);
	const { showErrorDialogHelper } = useDialogModalRedux();
	const { syntheticId } = useGoogleAnalyticsContext();
	const { sessionId } = useAppSelector(selectWebStoreState);
	const { backendUri } = useEnvContext();
	const platform = getPlatform();
	/* eslint-disable-next-line */
	const [debugUserData, setDebugUserData] = useState<IUserTracker | null>(
		userTracker
	);
	const dispatch = useDispatch();

	const handleRefresh = async () => {
		dispatch(isDebugEnabled());
		await forceReloadStoreRaw();
		await syncUserTracker();
	};

	//TODO:bfloyd
	const getDebugUserData = async (): Promise<void> => {
		if (!authToken?.value) {
			// showErrorDialog("no_auth_token_found", "No auth token found");
			console.warn("no_auth_token_found", "No auth token found");
			logout();
			return;
		}

		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				Authorization: `Bearer ${authToken?.value}`,
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			})
		};
		// TODO: figure out why this TS isn't happy
		// axios.get<IUserTrackerResponse>(uri, extraConfig)
		const uri = "/rest/debug/user/data";
		await axios
			.get<IUserDataResponse>(uri, requestConfig)
			.then(async (response) => {
				if (response.data && response.data.success) {
					setDebugUserData(response.data.userData.userTracker);
				} else {
					handleErrorResponse(response.data);
				}

				return response.data;
			})
			.catch((err) => {
				debugLogError("getDebugUserData", err);
				showErrorDialogHelper(err);
			});
	};

	const invalidateOffersTag = () => {
		dispatch(offersApi.util.invalidateTags(["offersTag"]));
	};
	const timeJump = async (
		nowTime: Moment | null,
		_deltaDays: number,
		_deltaHours?: number,
		_deltaMinutes?: number
	): Promise<boolean> => {
		const queryParams: {
			dateTime?: string;
			deltaDays?: number;
		} = {};
		if (nowTime) {
			const userDateTime = DateTime.fromISO(nowTime.toISOString());
			queryParams.dateTime = userDateTime
				.plus({
					days: _deltaDays ?? 0,
					hours: _deltaHours ?? 0,
					minutes: _deltaMinutes ?? 0
				})
				.toISO();
		} else {
			queryParams.deltaDays = _deltaDays;
		}
		let wasSuccessful = false;
		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				Authorization: `Bearer ${authToken?.value}`,
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			}),
			params: queryParams
		};
		const uri = "/rest/debug/user/timeJump";
		await axios
			.post<IAbstractResponse>(uri, {}, requestConfig)
			.then((response) => {
				if (response.data && response.data.success) {
					wasSuccessful = true;
					if (queryParams.dateTime) {
						try {
							localStorage.setItem(
								LOCAL_STORAGE_DATE_JUMPED,
								queryParams.dateTime
							);
						} catch (error) {
							console.warn("Unable to save the jumped time", error);
						}
					}
					if (setNowTime && response.headers["x-gsn-webstore-now"]) {
						setNowTime(response.headers["x-gsn-webstore-now"]);
					}
					handleRefresh();
					invalidateOffersTag();
					return response.data;
				} else {
					handleErrorResponse(response.data);
				}
			})
			.catch((err) => {
				debugLogError("timeJump", err);
				showErrorDialogHelper(err);
			});

		if (wasSuccessful) {
			void getDebugUserData().catch((err) => {
				debugLogError("getDebugUserData", err);
			});
		}

		return wasSuccessful;
	};

	const resetTimeJump = async (): Promise<boolean> => {
		let wasSuccessful = false;
		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				Authorization: `Bearer ${authToken?.value}`,
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			})
		};
		const uri = "/rest/debug/user/resetTimeJump";
		await axios
			.post<IAbstractResponse>(uri, {}, requestConfig)
			.then((response) => {
				if (response.data && response.data.success) {
					wasSuccessful = true;
					if (setNowTime && response.headers["x-gsn-webstore-now"]) {
						setNowTime(response.headers["x-gsn-webstore-now"]);
					}
					handleRefresh();
					invalidateOffersTag();
					return response.data;
				} else {
					handleErrorResponse(response.data);
				}
			})
			.catch((err) => {
				debugLogError("resetTimeJump", err);
				showErrorDialogHelper(err);
			});

		if (wasSuccessful) {
			void getDebugUserData().catch((err) => {
				debugLogError("getDebugUserData", err);
			});
		}
		return wasSuccessful;
	};

	const setAuthTokenTtl = async (authTokenTtl: number): Promise<boolean> => {
		if (!fbAccessToken) {
			/* eslint-disable */
			showErrorDialogHelper({
				name: "no_facebook_token",
				message: "Please logout and login again with Facebook"
			} as any);
			/* eslint-enable */

			return false;
		}

		const queryParams: {
			facebookAccessToken: string;
			ttlSeconds: number;
		} = {
			facebookAccessToken: fbAccessToken,
			ttlSeconds: authTokenTtl
		};
		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			}),
			params: queryParams
		};
		const uri = "/rest/user/authToken";
		return await axios
			.post<IAuthResponse>(uri, {}, requestConfig)
			.then((response) => {
				if (response.data && response.data.success) {
					const { value, expiresAt } = response.data;
					setAuthToken({ value, expiresAt });
					return true;
				} else {
					handleErrorResponse(response.data);
					return false;
				}
			})
			.catch((err) => {
				debugLogError("setAuthTokenTtl", err);
				showErrorDialogHelper(err);
				return false;
			});
	};

	const clearInboxMessages = async (): Promise<boolean> => {
		return await clearServerData("/rest/debug/user/clearInboxMessages");
	};

	const clearFirstLoginReward = async (): Promise<boolean> => {
		return await clearServerData("/rest/debug/user/clearFirstLoginReward");
	};

	const clearUser = async (): Promise<boolean> => {
		return await clearServerData("/rest/debug/user/clear");
	};

	const enableDebugAccess = async (): Promise<void> => {
		await clearServerData("/rest/debug/user/enable-debug-access");
	};

	const clearServerData = async (uri: string): Promise<boolean> => {
		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				Authorization: `Bearer ${authToken?.value}`,
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			})
		};
		return await axios
			.post<IAuthResponse>(uri, {}, requestConfig)
			.then((response) => {
				if (!response.data || !response.data.success) {
					handleErrorResponse(response.data);
				}
				syncUserTracker();
				return true;
			})
			.catch((err) => {
				debugLogError("clearServerData", err);
				showErrorDialogHelper(err);
				return false;
			});
	};

	const setLoyaltyPoints = async (points: number): Promise<void> => {
		const queryParams: {
			value: number;
		} = {
			value: points
		};
		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				"Content-Type": "application/json; charset=utf-8",
				Authorization: `Bearer ${authToken?.value}`,
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			}),
			params: queryParams
		};
		const uri = "/rest/debug/user/setLoyaltyPoints";
		await axios
			.post<IAbstractResponse>(uri, {}, requestConfig)
			.then((response) => {
				if (response.data && response.data.success) {
					handleRefresh();
					return response.data;
				} else {
					handleErrorResponse(response.data);
				}
			})
			.catch((err) => {
				debugLogError("setLoyaltyPoints", err);
				showErrorDialogHelper(err);
			});

		await getDebugUserData();
	};

	const setServerTime = async (now: string): Promise<boolean> => {
		let wasSuccessful = false;
		const queryParams: {
			dateTime: string;
		} = {
			dateTime: now
		};
		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders({
				"Content-Type": "application/json; charset=utf-8",
				Authorization: `Bearer ${authToken?.value}`,
				"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
				"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
				"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
				"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
				"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
					? "pwa"
					: "web",
				"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
					.incrementAndGetSequence()
					.toString(),
				"X-SCOPELY-WEBSTORE-PLATFORM": platform,
				"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
				"X-TITAN-METADATA": JSON.stringify({
					app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
					environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
				})
			}),
			params: queryParams
		};
		const uri = "/rest/debug/user/timeJump";
		await axios
			.post<IAbstractResponse>(uri, {}, requestConfig)
			.then((response) => {
				if (response.data && response.data.success) {
					wasSuccessful = true;
					if (setNowTime && response.headers["x-gsn-webstore-now"]) {
						setNowTime(response.headers["x-gsn-webstore-now"]);
					}
					handleRefresh();
					invalidateOffersTag();
					return response.data;
				} else {
					handleErrorResponse(response.data);
				}
			})
			.catch((err) => {
				debugLogError("setServerTime", err);
				showErrorDialogHelper(err);
			});

		if (wasSuccessful) {
			void getDebugUserData().catch((err) => {
				debugLogError("getDebugUserData", err);
			});
		}
		return wasSuccessful;
	};

	return (
		<DebugWebStoreContext.Provider
			value={{
				timeJump,
				resetTimeJump,
				setLoyaltyPoints,
				setServerTime,
				setAuthTokenTtl,
				authToken,
				clearInboxMessages,
				clearFirstLoginReward,
				clearUser,
				enableDebugAccess,
				handleRefresh
			}}
		>
			{children}
		</DebugWebStoreContext.Provider>
	);
}

export default DebugWebStoreProvider;
